import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
	ViewContainerRef,
	inject,
	signal,
} from '@angular/core';
import { FileValidation } from '@shared/ui-components/au-file-loader/helpers/file-validation.class';
import { filter, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { FileViewerModalComponent } from '@shared/ui-components/au-file-loader/file-viewer-modal/file-viewer-modal.component';
import {
	IAuFileMetadata,
	IDroppedFile,
	IFileTypeConfig,
	IFileUploadEvent,
	IUploadOptions,
} from './au-file-loader.interfaces';
import { FileUtilities } from './helpers/file-utilities';
import { AuUtilsFunctions } from '@app/shared/au-utils';
import { FormControl } from '@angular/forms';
import { AuDialogService } from '@core/services/au-dialog.service';
import { AuErrorService } from '@core/services/au-error-service';
import {
	FileLoaderService,
	IFileLoaderOptions,
} from '@shared/ui-components/au-file-loader/services/file-loader.service';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { fileTypesConfig } from '@shared/ui-components/au-file-loader/au-file-loader.config';
import { FileListTools } from '@shared/ui-components/au-file-loader/helpers/file-list-helpers';
import { FileSelectionsService } from '@shared/ui-components/au-file-loader/services/file-selections.service';
import { TableType } from '@app/pages/cmms/types';
import { CmmsSharedService } from '@app/pages/cmms/services/cmms-shared.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuExpansionPanelComponent } from '../au-expansion-panel/au-expansion-panel.component';
import { AsyncPipe } from '@angular/common';
import { OngoingCounterComponent } from './au-file-load-floating-window/ongoing-counter/ongoing-counter.component';
import { MatIcon } from '@angular/material/icon';
import { MatMiniFabButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { AuNativeInputComponent } from '../au-native-input/au-native-input/au-native-input.component';
import { MatTooltip } from '@angular/material/tooltip';
import { AuFilesListItemComponent } from './files-list-item/au-files-list-item.component';
import { FileCardsListComponent } from './file-cards-list/file-cards-list.component';
import { FilesToolbarComponent } from './files-toolbar/files-toolbar.component';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';

@Component({
	selector: 'au-file-loader',
	templateUrl: './au-file-loader.component.html',
	styleUrls: ['./au-file-loader.component.scss'],
	imports: [
		AuExpansionPanelComponent,
		OngoingCounterComponent,
		MatIcon,
		MatMiniFabButton,
		MatMenuTrigger,
		AuNativeInputComponent,
		MatTooltip,
		AuFilesListItemComponent,
		FileCardsListComponent,
		FilesToolbarComponent,
		MatMenu,
		MatMenuItem,
		AsyncPipe,
		NgxTolgeeModule,
	],
})
export class AuFileLoaderComponent
	extends FileListTools
	implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
	private readonly fileLoader = inject(FileLoaderService);
	private readonly dialog = inject(MatDialog);
	private readonly auDialogService = inject(AuDialogService);
	private readonly auErrorService = inject(AuErrorService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly selectionsService = inject(FileSelectionsService);
	private readonly cmmsSharedService = inject(CmmsSharedService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly translateService = inject(TranslateService);

	@Input() askDeleting = true;
	@Input() options: Partial<IFileLoaderOptions>;
	@Input() loadedFiles: IAuFileMetadata[];
	@Input() relatedFiles: IAuFileMetadata[];
	@Input() disableBulkSelections = false;
	@Input() enableFileListScroll = false;
	@Input() hideCounter = false;
	@Input() restrictFileSourceMenu = false;
	@Input() showFileTypesHint = false;
	@Input() fullFileLoadData = false;
	@Input() uploadTitle: string;
	@Input() cardsView = false;
	@Input() allowEditFiles = true;
	@Input() buttonOnly = false;

	/**
	 * Flag that show when we delegate unlink files to parent component, without removing file from system
	 */
	@Input() delegateDeleting = false;

	@Output() filesLoaded = new EventEmitter<string[] | any[]>();
	@Output() fullFileLoaded = new EventEmitter<Partial<IDroppedFile>[]>();
	@Output() filesDeleted = new EventEmitter<{
		ids: string[];
		onSuccess: () => void;
	}>();
	@Output() controlReady = new EventEmitter<FormControl>();
	@Output() fileListChanged = new EventEmitter<Partial<IDroppedFile>[]>();
	@Output() addExistingFileClicked = new EventEmitter<{
		page: CmmsPages;
		tableType: TableType;
	}>();
	@Output() rowSelect = new EventEmitter<any>();

	formControl = new FormControl([], []);
	fileCouldBeDropped: boolean;
	filesList = signal<Partial<IDroppedFile>[]>([]);
	filteredFileList: Partial<IDroppedFile>[] = [];
	filteredRelatedFiles: Partial<IDroppedFile>[] = [];
	searchValue: string;
	niceFormattedFileTypes: string;
	fileValidation: FileValidation;
	fileTypesConfig: IFileTypeConfig = fileTypesConfig;
	selection = this.selectionsService.selections;
	selections$: Observable<string[]>;
	relatedFileSelected: boolean;

	@ViewChild('fileInput', { read: ViewContainerRef })
	private fileInputRef: ViewContainerRef;

	private loadersCounter = 0;

	constructor() {
		super();
	}

	ngOnInit() {
		this.controlReady.emit(this.formControl);
		this.selections$ = this.selectionsService.selections$;
		this.trackIfRelatedFileSelected();

		this.fileTypesConfig = this.options?.fileTypesConfig || fileTypesConfig;
		this.fileValidation = new FileValidation(this.fileTypesConfig);
		this.niceFormattedFileTypes = this.fileValidation.getNiceFormattedFileTypes();
		this.cdr.markForCheck();

		this.cmmsSharedService.newFile
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(shouldNewFile => {
				if (shouldNewFile) {
					this.fileInputRef.element.nativeElement.click();
				}
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.loadedFiles) {
			this.pushNewFilesFromChangesToFileList(changes.loadedFiles.currentValue);
			this.filteredFileList = [...this.filesList()];
			this.searchValue = '';
		}

		if (changes.relatedFiles) {
			this.relatedFiles = changes.relatedFiles.currentValue
				.map(FileUtilities.makeObjectForExistingFile)
				.sort(FileUtilities.sortFilesByDate);
			this.filteredRelatedFiles = [...this.relatedFiles];
			this.searchValue = '';
		}
		if (changes.options?.currentValue?.fileTypesConfig) {
			this.fileTypesConfig = changes.options.currentValue.fileTypesConfig;
			this.fileValidation = new FileValidation(this.fileTypesConfig);
			this.niceFormattedFileTypes = this.fileValidation.getNiceFormattedFileTypes();
			this.cdr.markForCheck();
		}
	}

	ngAfterViewInit() {
		if (this.options?.instantlyOpenSelectionDialog) {
			this.fileInputRef.element.nativeElement.click();
		}
	}

	onFileChanged(event) {
		const files = FileUtilities.getFilesFromEvent(event);

		for (let i = 0; i < files.length; i++) {
			this.startLoadProcedure(files[i]);
		}
	}

	clearSelectionPressed() {
		this.selectionsService.selectionClear$.next();
	}

	private startLoadProcedure(file: File): void {
		const errors = this.fileValidation.getFileValidationErrors(file);
		const fileObject: Partial<IDroppedFile> = FileUtilities.makeObjectForNewFile(
			file,
			errors
		);
		this.filesList.update(currentFiles => [...currentFiles, fileObject]);
		const lastIndex = this.lastFileIndex;

		if (AuUtilsFunctions.objectNotEmpty(errors)) {
			this.fileCouldBeDropped = false;
			fileObject.requestBrakeSubject.complete();

			return;
		}

		this.filesList()[lastIndex].file = file;
		this.startHttpLoading(file);
	}

	private startHttpLoading(file: File, index = this.lastFileIndex) {
		const requestBrakeSubject$ = this.filesList()[index].requestBrakeSubject;
		const uploadOptions: IUploadOptions = {
			file,
			index,
			requestBrakeSubject$: requestBrakeSubject$,
			mutation: this.options?.mutation,
			mutationVariables: this.options?.mutationVariables,
			endpointName: this.options?.endpointName,
		};

		this.loadersCounter++;
		this.fileLoader
			.upload(uploadOptions)
			.pipe(
				tap((data: IFileUploadEvent) => this.handleOngoingEvent(data)),
				filter((data: IFileUploadEvent) => FileUtilities.finishEvent(data))
			)
			.subscribe({
				next: response => this.handleFinishEvent(response),
				error: error => this.handleErrorEvent(error),
			});
	}

	private handleFinishEvent(response: IFileUploadEvent): void {
		this.loadersCounter--;
		this.updateFileInFilesList(response);

		if (this.loadersCounter === 0 && this.onlyNewlyDownloadedFiles.length) {
			this.emitChangesOutside();
		}
	}
	private handleErrorEvent(error): void {
		this.loadersCounter--;
		console.error(error);
	}

	private handleOngoingEvent(data: { event: any; index: number }): void {
		if (data.event.error) {
			this.handleHttpError(data.event.error);
		}

		if (FileUtilities.progressEvent(data) && !!this.filesList()[data.index]) {
			this.handleProgressEvent(data);
		}

		this.cdr.detectChanges();
	}

	deleteItemFromFileList(index: number) {
		if (this.filesList()[index].newlyLoaded) {
			this.removeFileByIndex(index);
		} else {
			// We need to ensure user that he deleted file loaded by someone else.
			// Because it's impossible to restore such a file
			if (this.askDeleting) {
				this.askUserAndRemoveFile(index);
			} else {
				this.removeFileByIndex(index);
			}
		}
	}

	async deleteMultipleItemsFromFileList() {
		const filePk = this.cardsView
			? this.selection.selected.map(file => file.pk)
			: await this.selectionsService.getSelections();

		if (this.hasOldAndMarkedForDeletionFile(filePk)) {
			this.askUserAndRemoveMultipleFiles(filePk);
		} else {
			this.removeMultipleFiles(filePk);
		}
	}

	private askUserAndRemoveMultipleFiles(selections): void {
		this.askUserAboutDeletion(selections.length).subscribe((confirm: boolean) => {
			if (confirm) {
				this.removeMultipleFiles(selections);
			}
		});
	}

	private askUserAboutDeletion(length: number): Observable<boolean> {
		const dialogRef = this.auDialogService.openDeleteDialog({
			itemType: this.translateService.instant('file'),
			description: this.translateService.instant(
				'files_section_delete_files_confirmation',
				{
					length: length,
				}
			),
		});

		return this.auDialogService.closedByEnter(dialogRef);
	}

	private askUserAndRemoveFile(index: number): void {
		const dialogRef = this.auDialogService.openDeleteDialog({
			itemType: this.translateService.instant('file'),
			description: this.translateService.instant(
				'files_section_delete_file_confirmation',
				{
					name: this.filesList()[index].name,
				}
			),
		});

		this.auDialogService.closedByEnter(dialogRef).subscribe((confirm: boolean) => {
			if (confirm) {
				this.removeFileByIndex(index);
			}
		});
	}

	private removeFileByIndex(index: number): void {
		if (this.delegateDeleting) {
			this.emitDeletion([this.filesList()[index].pk]);
		} else {
			if (!this.filesList()[index].pk) {
				this.removeItemFromFileList(index);
				return;
			}

			this.showDeletionLoader(index);
			this.deleteFile(index);
		}
	}

	private deleteFile(index: number): void {
		const pk = this.filesList()[index].pk;

		this.fileLoader.remove([pk]).subscribe({
			next: () => {
				this.onDeleteSuccess(index, pk);
			},
			error: error => {
				this.onDeleteError(index, error);
			},
		});
	}

	private onDeleteSuccess(index: number, pk: string): void {
		this.hideDeletionLoader(index);
		this.emitDeletion([pk]);
		this.removeItemFromFileList(index);
	}

	private onDeleteError(index: number, error: any): void {
		this.hideDeletionLoader(index);
		this.auErrorService.notifyUserAboutError({
			error,
			defaultMessage: this.translateService.instant('files_section_cant_delete_file'),
		});
	}

	private removeMultipleFiles(selections: string[]) {
		if (this.delegateDeleting) {
			this.emitDeletion(selections);
		} else {
			this.showDeleteLoading(selections);
			const removeSubscription = this.removeFiles(selections);

			removeSubscription.subscribe({
				next: () => {
					this.handleSuccessfulRemoval(selections);
				},
				error: error => {
					this.handleFailedRemoval(selections, error);
				},
			});
			removeSubscription.subscribe({
				next: () => {
					this.handleSuccessfulRemoval(selections);
				},
				error: error => {
					this.handleFailedRemoval(selections, error);
				},
			});
		}
	}

	private removeFiles(selections: string[]): Observable<any> {
		return this.fileLoader.remove(selections);
	}

	private handleSuccessfulRemoval(selections: string[]) {
		if (this.delegateDeleting) {
			this.emitDeletion(selections);
		} else {
			this.filesList.set(this.filesList().filter(file => !selections.includes(file.pk)));
			this.emitDeletion(selections);
			this.clearAllDeletionLoaders(selections);
		}
	}

	private handleFailedRemoval(selections: string[], error: any) {
		this.auErrorService.notifyUserAboutError({
			error,
			defaultMessage: this.translateService.instant('files_section_cant_delete_file'),
		});
		this.clearAllDeletionLoaders(selections);
	}

	openFile(index: number) {
		if (this.loadedFiles) {
			const pk = this.filesList()[index].pk;
			return this.rowSelect.emit(this.loadedFiles.find(file => file.pk === pk));
		}

		return this.dialog.open(FileViewerModalComponent, {
			data: {
				files: this.filesList().filter(file => file.pk),
				index: index,
			},
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal', 'no-max-width'],
		});
	}

	openRelatedFile(fileData: { file: Partial<IDroppedFile>; index: number }) {
		return this.dialog.open(FileViewerModalComponent, {
			data: {
				files: this.relatedFiles.filter(file => file.pk),
				index: fileData.index,
			},
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal', 'no-max-width'],
		});
	}

	private emitChangesOutside() {
		if (this.fullFileLoadData) {
			this.fullFileLoaded.emit(this.filesList());
		} else {
			this.filesLoaded.emit(this.onlyNewlyDownloadedFiles);
		}

		this.formControl.setValue(this.onlyNewlyDownloadedFiles);
		this.fileListChanged.emit(this.filesList());
		this.filesList().forEach(file => (file.notEmittedYet = false));
	}

	private emitDeletion(ids: string[]) {
		this.filesDeleted.emit({
			ids,
			onSuccess: () => {
				this.selectionsService.removeSelections(ids);
			},
		});
	}

	fileTypesConfigToFileTypes(): string {
		return Object.values(this.fileTypesConfig)
			.flatMap(fileTypeConfig => fileTypeConfig.supportedFormatsList)
			.map(format => `.${format}`)
			.join(',');
	}

	onDragFileEnter(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.fileCouldBeDropped = true;
	}

	onDragFileLeave(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.fileCouldBeDropped = false;
	}

	onDragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		event.dataTransfer.dropEffect = 'link';
		// this.fileCouldBeDropped = true;
	}

	handleImageDrop(event: DragEvent) {
		event.preventDefault();
		this.fileCouldBeDropped = false;

		this.onFileChanged(event.dataTransfer);
	}

	addButtonClick(event: Event, fileInput: HTMLElement) {
		event.preventDefault();
		fileInput.click();
	}

	retryLoadFile(index) {
		this.startHttpLoading(this.filesList()[index].file, index);
	}

	searchHandler(search: string) {
		if (search) {
			const loverCaseSearch = search.toLowerCase();
			const fileFiltrationCallback = file => {
				return (
					(file.type &&
						this.getFileTypeName(file.type).toLowerCase().includes(loverCaseSearch)) ||
					file.name.toLowerCase().includes(loverCaseSearch)
				);
			};

			this.filteredFileList = this.filesList().filter(fileFiltrationCallback);
			this.filteredRelatedFiles = this.relatedFiles.filter(fileFiltrationCallback);
		} else {
			this.filteredFileList = [...this.filesList()];
			this.filteredRelatedFiles = [...this.relatedFiles];
		}
	}

	trackIfRelatedFileSelected() {
		this.selection.changed.subscribe(selectionChange => {
			if (!this.relatedFiles) {
				return;
			}

			let isSelected = false;
			for (let i = 0; i < this.relatedFiles.length; i++) {
				if (selectionChange.source.isSelected(this.relatedFiles[i])) {
					isSelected = true;
					break;
				}
			}
			this.relatedFileSelected = isSelected;
		});
	}

	addExisting() {
		this.addExistingFileClicked.emit({
			page: CmmsPages.existingEntities,
			tableType: TableType.FILES,
		});
	}

	ngOnDestroy() {
		this.selectionsService.selections.clear();
	}
}
